import './styles.scoped.css'
import {useEffect} from 'react'
import {
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    Link,
    Avatar,
    Button,
    Autocomplete,
    Chip,
    IconButton,
} from '@mui/material'
import {UploadFile, Upload, Edit, Delete, Cancel} from '@mui/icons-material'
import {Controller} from 'react-hook-form'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {useDropzone} from 'react-dropzone'
import {useMobile} from '../../../../hooks'

export default ({form, avatarUrl, setAvatarUrl, file, setFile, branches, selectedBranches, setSelectedBranches}) => {
    const {mobile} = useMobile()
    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        // No SVG: it can carry script, and the mobile app can't render it.
        accept: {'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/gif': ['.gif'], 'image/webp': ['.webp']},
        noClick: true,
        multiple: false,
        maxSize: 3 * 1024 * 1024,
        onDrop: acceptedFiles => {
            setFile(acceptedFiles[0])
            setAvatarUrl(URL.createObjectURL(acceptedFiles[0]))
        },
    })

    const genders = [
        {key: 'male', value: 'Male'},
        {key: 'female', value: 'Female'},
        {key: 'other', value: 'Other'},
        {key: 'prefer_not_to_say', value: 'Prefer Not to Say'},
    ]

    const maritalStatus = [
        {key: 'married', value: 'Married'},
        {key: 'single', value: 'Single'},
        {key: 'divorced', value: 'Divorced'},
        {key: 'widowed', value: 'Widowed'},
        {key: 'separated', value: 'Separated'},
    ]

    const ethnicities = [
        {key: 'africa', value: 'Africa'},
        {key: 'asia', value: 'Asia'},
        {key: 'europe', value: 'Europe'},
    ]

    useEffect(() => {
        form.setValue(
            'branch_ids',
            selectedBranches.map(lang => lang.id),
        )
        if (selectedBranches?.length == 1 && !selectedBranches[0].is_primary) {
            selectAsPrimary(selectedBranches[0].id)
            form.setValue('primary_branch_id', selectedBranches[0].id)
        } else {
            form.setValue('primary_branch_id', selectedBranches.find(a => a.is_primary)?.id)
        }
    }, [selectedBranches])

    const removeFile = async () => {
        setFile(null)
        setAvatarUrl(null)
    }

    const selectAsPrimary = id => {
        setSelectedBranches(prev =>
            prev.map(b => ({
                ...b,
                is_primary: b.id === id,
            })),
        )
    }

    const deleteItem = id => {
        setSelectedBranches(prev => {
            const updated = prev.filter(b => b.id !== id)
            const wasPrimary = prev.find(b => b.id === id)?.is_primary
            if (wasPrimary && updated?.length > 0) {
                updated[0].is_primary = true
            }
            return updated
        })
    }

    return (
        <>
            <Typography variant="overline" color="text.secondary">
                DEMOGRAPHICS
            </Typography>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Branch Placement{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <Autocomplete
                multiple
                options={branches}
                value={selectedBranches}
                onChange={(e, newVal) => setSelectedBranches(newVal)}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                getOptionLabel={opt => opt.branch_name}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder="Select Branches"
                        {...form.register('branch_ids_helper')}
                        error={!!form.formState.errors['branch_ids_helper']}
                        helperText={form.formState.errors['branch_ids_helper']?.message}
                    />
                )}
                slotProps={{chip: {sx: {display: 'none'}}}}
            />
            <div className="selected-items-container">
                {selectedBranches?.map((item, index) => (
                    <div className="selected-items" key={index}>
                        <Typography variant="chipLabel" color="text.primary">
                            {item.branch_name}
                        </Typography>
                        <div className="selected-items-actions">
                            {item?.is_primary ? (
                                <Chip label="Primary" color="primary" size="small" />
                            ) : (
                                <Link href="#" underline="hover" color="primary" onClick={() => selectAsPrimary(item?.id)}>
                                    <Typography variant="chipLabel">Set as primary</Typography>
                                </Link>
                            )}
                            <IconButton onClick={() => deleteItem(item?.id)}>
                                <Cancel color="disabled" />
                            </IconButton>
                        </div>
                    </div>
                ))}
            </div>
            <br />
            <hr className="line" />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Profile Picture
            </Typography>
            <div style={{display: avatarUrl ? 'flex' : 'none', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                <Avatar src={avatarUrl} style={{height: 130, width: 130}} />
                <Button variant="outlined" startIcon={<Edit />} onClick={open}>
                    Replace
                </Button>
                <Button variant="outlined" startIcon={<Delete />} color="error" onClick={removeFile}>
                    Remove
                </Button>
            </div>
            <div
                {...getRootProps()}
                style={{
                    width: '50%',
                    backgroundColor: isDragActive ? '#e3f2fd' : '#ffffff',
                    display: avatarUrl || mobile ? 'none' : 'block',
                    borderColor: isDragActive ? '#3f51b5' : '#0000001F',
                    border: '1px dashed #0000001F',
                    borderRadius: 1,
                    height: 150,
                    minWidth: 300,
                    boxSizing: 'border-box',
                    textAlign: 'center',
                }}>
                <input {...getInputProps()} />
                <br />
                <UploadFile style={{fontSize: 26}} color="primary" />
                <Typography variant="body1" color="text.primary" style={{marginTop: 10, marginBottom: 10}}>
                    <Link underline="always" onClick={open} style={{cursor: 'pointer'}}>
                        {'Click to upload '}
                    </Link>
                    or drag and drop
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    PNG, JPG, GIF or WebP (max. 3MB)
                </Typography>
            </div>
            {!avatarUrl && mobile && (
                <>
                    <br />
                    <Button variant="outlined" startIcon={<Upload />} onClick={open}>
                        Upload Photo
                    </Button>
                    <br />
                </>
            )}
            <br />
            <div className="form">
                <div className="form-left">
                    <Typography variant="inputLabel" color="text.secondary">
                        First Name{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <TextField
                        placeholder="Enter your first name"
                        fullWidth
                        {...form.register('firstName')}
                        error={!!form.formState.errors['firstName']}
                        helperText={form.formState.errors['firstName']?.message}
                    />
                </div>
                <div className="form-right">
                    <Typography variant="inputLabel" color="text.secondary">
                        Last Name{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <TextField
                        placeholder="Enter your last name"
                        fullWidth
                        {...form.register('lastName')}
                        error={!!form.formState.errors['lastName']}
                        helperText={form.formState.errors['lastName']?.message}
                    />
                </div>
            </div>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Initial
            </Typography>
            <TextField placeholder="Enter your initial" fullWidth {...form.register('initial')} />
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Email{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <TextField
                placeholder="Enter your email"
                fullWidth
                {...form.register('email')}
                error={!!form.formState.errors['email']}
                helperText={form.formState.errors['email']?.message}
            />
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Gender{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <FormControl fullWidth error={!!form.formState.errors['gender']}>
                <Controller
                    {...form.register('gender')}
                    control={form.control}
                    render={({field}) => (
                        <Select
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value)}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                                anchorOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                            }}
                            displayEmpty
                            renderValue={selected => {
                                if (selected === '') {
                                    return <span style={{color: '#aaa'}}>Select your gender</span>
                                }
                                return genders.find(item => item.key === selected)?.value ?? selected
                            }}>
                            {genders.map(item => (
                                <MenuItem key={item?.key} value={item?.key}>
                                    {item?.value}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
                <FormHelperText>{form.formState.errors['gender']?.message}</FormHelperText>
            </FormControl>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                SSN{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <TextField
                placeholder="Enter your SSN"
                fullWidth
                {...form.register('ssn')}
                error={!!form.formState.errors['ssn']}
                helperText={form.formState.errors['ssn']?.message}
            />
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Date of Birth{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <Controller
                {...form.register('dateOfBirth')}
                control={form.control}
                render={({field}) => (
                    <DatePicker
                        placeholder="Enter your date of birth"
                        {...field}
                        value={field.value ?? null}
                        onChange={newValue => field.onChange(newValue)}
                        enableAccessibleFieldDOMStructure={false}
                        slots={{
                            textField: params => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    error={!!form.formState.errors['dateOfBirth']}
                                    helperText={form.formState.errors['dateOfBirth']?.message}
                                />
                            ),
                        }}
                    />
                )}
            />
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Marital Status
            </Typography>
            <FormControl fullWidth>
                <Controller
                    {...form.register('maritalStatus')}
                    control={form.control}
                    render={({field}) => (
                        <Select
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value)}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                                anchorOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                            }}
                            displayEmpty
                            renderValue={selected => {
                                if (selected === '') {
                                    return <span style={{color: '#aaa'}}>Select your marital status</span>
                                }
                                return maritalStatus.find(item => item.key === selected)?.value ?? selected
                            }}>
                            {maritalStatus.map(item => (
                                <MenuItem key={item?.key} value={item?.key}>
                                    {item?.value}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Ethnicity
            </Typography>
            <FormControl fullWidth>
                <Controller
                    {...form.register('ethnicity')}
                    control={form.control}
                    render={({field}) => (
                        <Select
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value)}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                                anchorOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                            }}
                            displayEmpty
                            renderValue={selected => {
                                if (selected === '') {
                                    return <span style={{color: '#aaa'}}>Select your ethnicity</span>
                                }
                                return ethnicities.find(item => item.key === selected)?.value ?? selected
                            }}>
                            {ethnicities.map(item => (
                                <MenuItem key={item?.key} value={item?.key}>
                                    {item?.value}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
        </>
    )
}
